import React, {useState, useCallback, useEffect} from "react";
import PropTypes from 'prop-types';

import { Typography, Box } from "@mui/material";

import { makeStyles } from "tss-react/mui";

import FaceMeasurer from "../../components/mugshot/FaceMeasurer/FaceMeasurer";

import { resMedError, resMedSuccess } from "../../components/patientTheme"

import { isMobile } from "../../components/Util"

import {
    MIN_FACE_HEIGHT_MM,
    MAX_FACE_HEIGHT_MM,
    MIN_NOSE_WIDTH_MM,
    MAX_NOSE_WIDTH_MM,
    MIN_NOSE_DEPTH_MM,
    MAX_NOSE_DEPTH_MM
} from "../../components/mugshot/FaceMeasurer/MugShotConfig";

import v2_client from "ui-common/api/OpenClient";
import {useTranslation} from "react-i18next";
import MaskSelectorToolStyles from "./MaskSelectorToolStyles";

import { saveScanImagesEnabled } from "../../AppSettings"

import { scrollToTop } from "ui-common/components/common";

const between = (x, min, max) => {
    return x >= min && x <= max;
}

const measurementsValid = (measurements) => {

    if ( ! between(measurements["faceHeight"], MIN_FACE_HEIGHT_MM, MAX_FACE_HEIGHT_MM) ) {
        return false;
    }

    if ( ! between(measurements["noseWidth"], MIN_NOSE_WIDTH_MM, MAX_NOSE_WIDTH_MM) ) {
        return false;
    }

    return between(measurements["noseLengthBest"], MIN_NOSE_DEPTH_MM, MAX_NOSE_DEPTH_MM);
}

const sendFailureData = async (scanFailInfo) => {
    return v2_client.post(`/scan-fail/`,
        scanFailInfo);
};

/**
 *
 * @param measurements The person's measurement data.
 *
 * @param images The image data that the measurement data was derived from.
 *
 * @param setErrors A setter for setting the error data structure.
 *
 * @param setFieldValue The setFieldValue method provided by Formik for setting data for a future form submit.
 *
 * @returns {boolean}  returns false if measurements are not in the required ranges and sets error timeout to true.
 *                     returns true if measurement data is copacetic.
 */
export const handleFaceMeasurerCompleteLogic = (measurements, images, setErrors, setFieldValue ) => {

    // We've set some constraints to verify that the face measurements are realistic.  If they are not in
    // expected ranges we redirect the user to the retry page with the generic timeout error message.
    if ( ! measurementsValid(measurements) ) {
        setErrors({ timeout: true });
        return false;
    }

    setFieldValue("front_image", images.front);
    setFieldValue("left_image", images.left);
    setFieldValue("right_image", images.right);

    setFieldValue("face_height_unit", "cm");
    setFieldValue("nose_depth_unit", "mm");
    setFieldValue("nose_width_unit", "cm");

    let faceHeight = measurements["faceHeight"] / 10;

    setFieldValue("face_height", faceHeight.toFixed(2) );
    setFieldValue("nose_depth", measurements["noseLengthBest"].toFixed(2) );
    setFieldValue("nose_width", (measurements["noseWidth"] / 10).toFixed(2) );

    setFieldValue("mugshot_measurements", true);
    setFieldValue("mugshot_scan_info", {
        scan_info: measurements.scan_info,
        angles: {
            front: {x: measurements.frontxAngle, y: measurements.frontyAngle },
            left: {x: measurements.leftxAngle, y: measurements.leftyAngle },
            right: {x: measurements.rightxAngle, y: measurements.rightyAngle },
        }
    });

    return true;
}


/**
 *
 * Processes a scanInfo object and adds derived information
 *
 * @param scanInfo data structure.  Below is a sample scanInfo data structure.
 *
 *
 {
     "timeout_length": 15000,
     "ideal_y_front_angle": 0,
     "ideal_y_left_angle": 20,
     "ideal_y_right_angle": -20,
     "y_angle_distance_variance": 4,
     "mouth_open_check_enabled": false,
     "blur_detection_enabled": true,
     "glass_check_enabled": true,
     "user_agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/100.0.4896.127 Safari/537.36",
     "is_mobile": "false",
     "head_pos_details": [
     {
                "face": true,
                "glasses": true,
                "scanTime": 1146,
                "x": "169.57",
                "y": "-2.19",
                "blurInfo": {
                    "width": 88,
                    "height": 103,
                    "num_edges": 479,
                    "edge_ratio": "0.0528",
                    "avg_edge_width": "2.52",
                    "avg_edge_width_perc": "2.86"
                },
                "normalizedX": "10.4"
            },
     ...
     ]
 }

 *
 *
 *
 * @returns {*&{
 *              y_angle_max: string,
 *              speed_mean: string,
 *              y_angle_min: string,
 *              speed_max: string,
 *              blur_edge_ratio_max: string,
 *              x_angle_normalized_min: string,
 *              x_angle_normalized_max: string,
 *              blur_edge_ratio_mean: string}
 *           }
 */

export const annotateScanInfo = (scanInfo) => {


    if ( scanInfo.head_pos_details === undefined ) {
        return scanInfo;
    }

    let prevScanTime = undefined;
    let prevX = undefined;
    let prevY = undefined;
    let maxX = undefined;
    let minX = undefined;
    let maxY = undefined;
    let minY = undefined;
    let maxSpeed = undefined;
    let speedSum = 0;
    let maxBlurEdgeRatio = undefined;
    let blurEdgeRatioSum = 0;

    for (const detailIndex in scanInfo.head_pos_details) {
        let detail = scanInfo.head_pos_details[detailIndex];
        let speed = undefined;

        // Normalize the X angle so that 0 degrees is forward
        let normalizedX = Number(detail.x) > 0 ? (180 - Number(detail.x)) : (-180 - Number(detail.x));
        detail.normalizedX = `${normalizedX.toPrecision(3)}`;

        // Calculate the speed in degrees per second.
        if(detail.scanTime !== undefined && prevScanTime !== undefined) {
            let imageIntervalSeconds = (detail.scanTime - prevScanTime) / 1000;
            let xDistance = normalizedX - prevX;
            let yDistance = detail.y - prevY;
            let totalDistance = Math.sqrt(Math.pow(xDistance, 2) + Math.pow(yDistance, 2));
            speed = totalDistance / imageIntervalSeconds;

            detail.speed = `${speed.toPrecision(3)}`;
        }

        // Figure out max and min angles, blur edge ratios and speed.
        if (maxX === undefined || maxX < normalizedX) {
            maxX = normalizedX;
        }

        if (minX === undefined || minX > normalizedX) {
            minX = normalizedX;
        }

        if (maxY === undefined || maxY < Number(detail.y)) {
            maxY = Number(detail.y);
        }

        if (minY === undefined || minY > Number(detail.y)) {
            minY = Number(detail.y);
        }

        if ( detail.blurInfo !== undefined ) {

            if (maxBlurEdgeRatio === undefined || maxBlurEdgeRatio < Number(detail.blurInfo.edge_ratio)) {
                maxBlurEdgeRatio = Number(detail.blurInfo.edge_ratio);
            }

            // Add to the blur ratio and speed sums used for calculating the means.
            blurEdgeRatioSum += Number(detail.blurInfo.edge_ratio);
        }

        if (speed !== undefined && (maxSpeed === undefined || maxSpeed < speed)) {
            maxSpeed = speed;
        }

        if (speed !== undefined) {
            speedSum += speed;
        }

        // Save the timestamp and angles for calculating the speed of the next image.
        prevScanTime = detail.scanTime;
        prevX = normalizedX;
        prevY = detail.y;
    }

    // Calculate the mean speed and blur edge ratio
    let meanBlurEdgeRatio = 0;
    if (scanInfo.head_pos_details.length > 0) {
        meanBlurEdgeRatio = blurEdgeRatioSum / scanInfo.head_pos_details.length;
    }

    let meanSpeed = 0;
    if (scanInfo.head_pos_details.length > 1) {
        meanSpeed = speedSum / (scanInfo.head_pos_details.length - 1);
    }

    return {
        ...scanInfo,
        x_angle_normalized_max: safeToPrecisionString(maxX,3),
        x_angle_normalized_min: safeToPrecisionString(minX,3),
        y_angle_max: safeToPrecisionString(maxY,3),
        y_angle_min: safeToPrecisionString(minY,3),
        blur_edge_ratio_max: safeToPrecisionString(maxBlurEdgeRatio,3),
        blur_edge_ratio_mean: safeToPrecisionString(meanBlurEdgeRatio,3),
        speed_max: safeToPrecisionString(maxSpeed,3),
        speed_mean: safeToPrecisionString(meanSpeed,3)
    };
}

const safeToPrecisionString = (value, precision) => {
    if ( value === undefined  ){
        return "";
    }

    try {
        return `${value.toPrecision(precision)}`;
    } catch (error) {
        return "";
    }
}

const MugShotMaskSizing = ({
  setFieldValue,
  handleSubmit,
  handleNext,
  model,
  modelLoadingPromise,
  setBackButtonOverride,
  setHelpButtonOverride,
  tokenData,
  scanAttempt,
  setScanAttempt,
  onUserMediaError,
  cameraId = undefined,
  setCameraId = undefined,
  showNoCameraAccessDialog = false,
  cameraInfoArray,
  setCameraInfoArray,
  requestTransition,
  setErrors
}) => {

  const {token, hme_org_code, hme_location_code, patient_code } = tokenData;

  const [glassesOn, setGlassesOn] = useState(false);
  const [showFailedState, setFailedState] = useState(false);
  const [showSuccessState, setSuccessState] = useState(false);
  
  const { t } = useTranslation("pages.MaskSelectorTool.MugShotMaskSizing");
  const removeGlasses = t("removeGlasses")

  useEffect(() => {
      scrollToTop();
      if (showFailedState === true) {
          requestTransition("FAILED")
      }
  }, [showFailedState]);

  const Styles = makeStyles()((theme) => ({

    outerContentBox: {
      position: "relative",
      top: 0,
      bottom: 0,
      marginBottom: 0,
      height: "100%",
    },

    successOuterContentBox: {
        background: resMedSuccess,
        width: "100svw",
        position: "absolute",
        left: 0,
        top: "53px",
        height: "calc(100svh - 53px)"
    },

    innerContentBox: {
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      maxWidth: 414,
      'margin': '0 auto',
    },

    descriptionText: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    
    button: {
      display: "flex",
      marginLeft: theme.spacing(1),
    },
    
    submittingDiv: {
      textAlign: "center",
      position: "relative",
      marginTop: "20px"
    },

     submittingDesc: {
       textAlign: "left",
       fontSize: "17px",
       lineHeight: "32px",
       marginBottom: "40px"
     },

     glassesErrorBanner: {
        position: "relative",
        top: 0,
        bottom: 0,
        zIndex: 200,
        width: "100%",
        minHeight: 54,
        background: resMedError,
        textAlign: "center",
      },

     errorBannerText: {
       fontSize: 38,
       color: "white"
     },

  }));

  const {classes} = Styles();
  const {classes: commonClasses} = MaskSelectorToolStyles();

  const handleFaceMeasurerComplete = useCallback(
      ({ measurements, images }) => {

        let success = handleFaceMeasurerCompleteLogic(measurements, images, setErrors, setFieldValue);

        if ( success === false ) {

            let context = {
                angles: {
                    front: {x: measurements.frontxAngle, y: measurements.frontyAngle },
                    left: {x: measurements.leftxAngle, y: measurements.leftyAngle },
                    right: {x: measurements.rightxAngle, y: measurements.rightyAngle },
                },
                errors: {
                    timeout: false,
                    glassesOn: false,
                    eyesClosed: false,
                    mouthOpen: false,
                },
                scan_info: {
                    ...measurements.scan_info,
                    measurements: {
                        faceHeight: measurements.faceHeight.toFixed(2),
                        noseWidth: measurements.noseWidth.toFixed(2),
                        noseLengthBest: measurements.noseLengthBest.toFixed(2),
                        noseLengthWeighted: measurements.noseLengthWeighted.toFixed(2),
                    },
                },
                frontImage: images.front,
                leftImage: images.left,
                rightImage: images.right,
            }

            let failureBody = buildFailureBody(context);
            failureBody.failure_info.measurementOutOfRange = true;

            setScanAttempt( scanAttempt + 1 );

            sendFailureData(failureBody).then(() => {
                // We don't check the response of a send failure
                // data message as we don't want any errors to interrupt
                // the flow.
            } );

            // For debugging in Safari.
            if (console.takeHeapSnapshot ) {
                console.takeHeapSnapshot("Failure " + scanAttempt);
            }


            setFailedState(true);
        } else {
            handleSubmit();
        }

      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [handleSubmit, scanAttempt, setErrors, setFieldValue, setScanAttempt]
  );

  const stateListener = (state) => {
      if( state === "analysing") {
          handleNext();
      }

      // This sets a green background colour for the successful scan page.
      if( state === "scanningCompletePause") {
          setSuccessState(true);
      } else {
          setSuccessState(false);
      }
  };

  const scanningStateListener = (state, context) => {

      if( state === "glasses") {
        setGlassesOn(true);
      } else {
        setGlassesOn(false);
      }

      if( state === "timeout" || state === "error")
      {
          let failureInfo = buildFailureBody(context);
          setScanAttempt( scanAttempt + 1 );

          sendFailureData(failureInfo).then(() => {
              // We don't check the response of a send failure
              // data message as we don't want any errors to interrupt
              // the flow.
          } );

          setErrors(context.errors);
          setFailedState(true);
      }

  };

  const buildFailureBody = (context) => {

      let annotedScanInfo = context.scan_info;

      try {
          annotedScanInfo = annotateScanInfo(context.scan_info)
      } catch (error ) {
          annotedScanInfo = context.scan_info;
      }

      let json_body = {
          "hme_org_code": hme_org_code,
          "hme_location_code": hme_location_code,
          "token": token,
          "attempt": scanAttempt,
          "is_mobile": isMobile(),
          "user_agent": navigator.userAgent,
          "angles": context.angles,
          "failure_info": {
              "timeout": context.errors.timeout,
              "glasses_on": context.errors.glassesOn,
              "eyes_closed": context.errors.eyesClosed,
              "mouth_open": context.errors.mouthOpen
          },
          "sizing_images": {},
          "patient_code": patient_code,
          "scan_info": annotedScanInfo,
      }

      if ( process.env.REACT_APP_ENVIRONMENT_DESC !== undefined) {
          json_body.environment = process.env.REACT_APP_ENVIRONMENT_DESC;
      }

      if ( saveScanImagesEnabled() ) {
          if ( context.frontImage !== undefined ) { json_body.sizing_images.front = context.frontImage; }
          if ( context.leftImage !== undefined ) { json_body.sizing_images.left = context.leftImage; }
          if ( context.rightImage !== undefined ) { json_body.sizing_images.right = context.rightImage; }
      }

      return json_body;
  }

  const scanningFailed = () => {
      setErrors({ timeout: true });
  }

    let outerClass = showSuccessState ?  classes.successOuterContentBox : classes.outerContentBox;

  return (
      <>
          {  !showFailedState && (

                <Box className={outerClass} >

                  <Box hidden={!glassesOn} className={classes.glassesErrorBanner}>
                        <Typography tabIndex="0" className={`${classes.errorBannerText} ${commonClasses.noOutline}`} aria-label={removeGlasses}>{removeGlasses}</Typography>
                  </Box>

                  <Box className={classes.innerContentBox}>
                    <FaceMeasurer
                        onComplete={handleFaceMeasurerComplete}
                        stateListener={stateListener}
                        scannerStateListener={scanningStateListener}
                        onFailed={scanningFailed}
                        model={model}
                        modelLoadingPromise={modelLoadingPromise}
                        setBackButtonOverride={setBackButtonOverride}
                        setHelpButtonOverride={setHelpButtonOverride}
                        imageCaptureListener={undefined}
                        cameraId={cameraId}
                        setCameraId={setCameraId}
                        onUserMediaError={onUserMediaError}
                        showNoCameraAccessDialog={showNoCameraAccessDialog}
                        cameraInfoArray={cameraInfoArray}
                        setCameraInfoArray={setCameraInfoArray}
                    />
                  </Box>
                </Box>
          )}
      </>  
  );
  
};
MugShotMaskSizing.propTypes = {
    setFieldValue: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleNext: PropTypes.func.isRequired,
    model: PropTypes.object,
    modelLoadingPromise: PropTypes.object,
    setBackButtonOverride: PropTypes.func,
    setHelpButtonOverride: PropTypes.func,
    tokenData: PropTypes.object.isRequired,
    scanAttempt: PropTypes.number,
    setScanAttempt: PropTypes.func,
    onUserMediaError: PropTypes.func,
    cameraId: PropTypes.string,
    setCameraId: PropTypes.func,
    showNoCameraAccessDialog: PropTypes.bool,
    cameraInfoArray: PropTypes.array,
    setCameraInfoArray:PropTypes.func,
    requestTransition: PropTypes.func,
    setErrors: PropTypes.func.isRequired
}

export default MugShotMaskSizing